import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Medida.css';

function Medida() {
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);
  const [columnaOrden, setColumnaOrden] = useState('idMedida');

  // Paginacion (server-side)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formulario, setFormulario] = useState({
    idMedida: '',
    nomMedida: '',
    estado: 1
  });

  // Importar Excel
  const inputImportarRef = useRef(null);
  const [importando, setImportando] = useState(false);

  const API_URL = '/api/medida';

  // Carga paginada desde el backend
  const cargarDatos = async (opts = {}) => {
    const _pagina    = opts.pagina    ?? pagina;
    const _busqueda  = opts.busqueda  ?? busqueda;
    const _col       = opts.col       ?? columnaOrden;
    const _asc       = opts.asc       ?? ordenAsc;
    const _porPagina = opts.porPagina ?? porPagina;
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: {
          busqueda: _busqueda,
          pagina: _pagina,
          porPagina: _porPagina,
          ordenCol: _col,
          ordenDir: _asc ? 'asc' : 'desc'
        }
      });
      const d = response.data;
      setDatosFiltrados(d.filas || []);
      setTotal(d.total || 0);
      setTotalPaginas(d.totalPaginas || 1);
      setPagina(d.pagina || 1);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar los datos');
    }
  };

  // Carga inicial
  useEffect(() => {
    cargarDatos({ pagina: 1 });
    // eslint-disable-next-line
  }, []);

  // Busqueda con debounce -> vuelve a pagina 1
  useEffect(() => {
    const t = setTimeout(() => {
      cargarDatos({ pagina: 1, busqueda });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [busqueda]);

  const ordenarPor = (columna) => {
    const esAsc = columnaOrden === columna ? !ordenAsc : true;
    setColumnaOrden(columna);
    setOrdenAsc(esAsc);
    cargarDatos({ pagina: 1, col: columna, asc: esAsc });
  };

  const irAPagina = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setPagina(p);
    cargarDatos({ pagina: p });
  };

  const cambiarPorPagina = (n) => {
    setPorPagina(n);
    setPagina(1);
    cargarDatos({ pagina: 1, porPagina: n });
  };

  // Exporta TODO lo que matchea la busqueda (no solo la pagina visible)
  const exportarExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/paginado`, {
        params: { busqueda, pagina: 1, porPagina: 100000,
                  ordenCol: columnaOrden, ordenDir: ordenAsc ? 'asc' : 'desc' }
      });
      const datosExportar = (response.data.filas || []).map(item => ({
        'ID': item.idMedida,
        'Nombre Medida': item.nomMedida,
        'Estado': item.estado === 1 ? 'Activo' : 'Inactivo'
      }));

      const ws = XLSX.utils.json_to_sheet(datosExportar);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Unidades de Medidas');
      XLSX.writeFile(wb, 'medidas.xlsx');
    } catch (e) {
      console.error(e);
      alert('Error al exportar');
    }
  };

  // ---------- Importar Excel ----------
  // Auto-detecta la fila de headers. Acepta columnas cuyo texto contenga
  // "medida" (id_medida / nom_medida).
  const abrirImportar = () => inputImportarRef.current?.click();

  const onArchivoImportar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const primerSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(primerSheet, { header: 1, defval: '' });

      let headerIdx = -1;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || [];
        const c0 = String(r[0] || '').toLowerCase();
        const c1 = String(r[1] || '').toLowerCase();
        if (c0.includes('medida') && c1.includes('medida')) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) {
        alert('No se encontro fila de encabezados. Debe tener columnas "id_medida" y "nom_medida".');
        return;
      }

      const filas = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const r = rows[i] || [];
        const id  = String(r[0] ?? '').trim();
        const nom = String(r[1] ?? '').trim();
        if (!id && !nom) continue;
        filas.push({ idMedida: id, nomMedida: nom });
      }

      if (filas.length === 0) {
        alert('El archivo no tiene filas de datos.');
        return;
      }

      const conf = window.confirm(
        `Se importaran ${filas.length} unidad(es) de medida. ` +
        `Los ID existentes se actualizaran; los nuevos se crearan con estado activo. ` +
        `No se eliminaran unidades. Continuar?`
      );
      if (!conf) return;

      const { data } = await axios.post('/api/medida/importar', filas);
      const lineas = [
        `Import completado.`,
        `  Creados:      ${data.creados}`,
        `  Actualizados: ${data.actualizados}`,
        `  Ignorados:    ${data.ignorados}`
      ];
      if (data.errores?.length) {
        lineas.push('', 'Errores/advertencias:');
        (data.errores.slice(0, 10)).forEach(m => lineas.push('  - ' + m));
        if (data.errores.length > 10) lineas.push(`  ... y ${data.errores.length - 10} mas`);
      }
      alert(lineas.join('\n'));
      cargarDatos();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al importar: ' + err.message);
    } finally {
      setImportando(false);
      if (inputImportarRef.current) inputImportarRef.current.value = '';
    }
  };

  const abrirModalNuevo = () => {
    setFormulario({ idMedida: '', nomMedida: '', estado: 1 });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (item) => {
    setFormulario({
      idMedida: item.idMedida,
      nomMedida: item.nomMedida,
      estado: item.estado
    });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setFormulario({ idMedida: '', nomMedida: '', estado: 1 });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: name === 'estado' ? parseInt(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modoEdicion) {
        await axios.put(`${API_URL}/${formulario.idMedida}`, formulario);
        alert('Registro actualizado exitosamente');
      } else {
        await axios.post(API_URL, formulario);
        alert('Registro creado exitosamente');
      }
      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Error al guardar el registro');
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta unidad de medida?')) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        alert('Unidad eliminada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar la unidad de medida');
      }
    }
  };

  return (
    <div className="medida-container">
      <div className="header-seccion">
        <h2>Gestión de Unidades de Medida</h2>
        <div className="acciones-header">
          <input
            type="text"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
          <button onClick={abrirModalNuevo} className="btn btn-nuevo">
            ➕ Nuevo
          </button>
          <button onClick={exportarExcel} className="btn btn-exportar">
            📊 Exportar Excel
          </button>
          <button onClick={abrirImportar} className="btn btn-exportar"
            disabled={importando}
            style={{ background: '#7B1FA2' }}
            title="Importar unidades desde Excel (upsert por id_medida)">
            {importando ? '⏳ Importando...' : '📥 Importar Excel'}
          </button>
          <input type="file" accept=".xlsx,.xls" ref={inputImportarRef}
            onChange={onArchivoImportar} style={{ display: 'none' }} />
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-datos">
          <thead>
            <tr>
              <th onClick={() => ordenarPor('idMedida')} className="th-ordenable">
                ID {columnaOrden === 'idMedida' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('nomMedida')} className="th-ordenable">
                Nombre {columnaOrden === 'nomMedida' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th onClick={() => ordenarPor('estado')} className="th-ordenable">
                Estado {columnaOrden === 'estado' && (ordenAsc ? '▲' : '▼')}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {datosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="6" className="sin-datos">No hay datos para mostrar</td>
              </tr>
            ) : (
              datosFiltrados.map(item => (
                <tr key={item.idMedida}>
                  <td>{item.idMedida}</td>
                  <td>{item.nomMedida}</td>
                  <td>
                    <span className={`badge ${item.estado === 1 ? 'badge-activo' : 'badge-inactivo'}`}>
                      {item.estado === 1 ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => abrirModalEditar(item)}
                      className="btn-accion btn-editar"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminar(item.idMedida)}
                      className="btn-accion btn-eliminar"
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="footer-tabla" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span>Total de registros: {total}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => irAPagina(1)} disabled={pagina === 1} className="btn-paginacion">«</button>
          <button onClick={() => irAPagina(pagina - 1)} disabled={pagina === 1} className="btn-paginacion">‹</button>
          <span style={{ fontSize: '13px' }}>Pagina {pagina} de {totalPaginas}</span>
          <button onClick={() => irAPagina(pagina + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">›</button>
          <button onClick={() => irAPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">»</button>
          <select value={porPagina} onChange={(e) => cambiarPorPagina(Number(e.target.value))}
            style={{ marginLeft: '8px', padding: '4px' }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
          </select>
        </div>
      </div>

      {/* Modal */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modoEdicion ? 'Editar Unidad de Medida' : 'Nuevo Unidad de Media'}</h3>
              <button onClick={cerrarModal} className="btn-cerrar">✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ID Medida *</label>
                <input
                  type="text"
                  name="idMedida"
                  value={formulario.idMedida}
                  onChange={handleChange}
                  maxLength="2"
                  required
                  disabled={modoEdicion}
                  className="form-input"
                  placeholder="Ingrese el ID de la Unidad de Medida"
                />
              </div>

              <div className="form-group">
                <label>Nombre de la Unidad de Medida *</label>
                <input
                  type="text"
                  name="nomMedida"
                  value={formulario.nomMedida}
                  onChange={handleChange}
                  maxLength="100"
                  required
                  className="form-input"
                  placeholder="Ingrese el nombre de la Unidad de Medida"
                />
              </div>

              <div className="form-group">
                <label>Estado *</label>
                <select
                  name="estado"
                  value={formulario.estado}
                  onChange={handleChange}
                  required
                  className="form-input"
                >
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-guardar">
                  {modoEdicion ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Medida;
